import { COREL_PROGRESS_SILENCE_TIMEOUT_MS } from "../../../config";
import { corelCommunication } from "../../../lib/corel/communication";
import { getActionTimeout } from "../../../lib/corel/timeoutService";
import { sendToTerminal, terminalLog } from "../../../lib/terminalLogger";
import { monitoradorObjetos } from "../../../lib/corel/MonitoradorObjetos";
import { notify } from "../../../lib/globalNotify";
import {
	enrichTargetsWithNames,
	extractSmbCredentials,
} from "./exportContextBuilder";
import { syncTargetsFromCorel } from "./targetSyncService";
import {
	buildBatchPayload,
	postBatchExport,
} from "./batchApi";
import { buildLayoutPngPayload } from "./layoutPayloadBuilder";
import { buildIdempotencyKey } from "../utils/idempotency";
import type { ExportFormat } from "../types";

export interface CorelExportRequest {
	action: string;
	requestId: string;
	data: Record<string, unknown>;
}

export interface ProductionBatchExportParams {
	savedData: Record<string, unknown>;
	format: ExportFormat;
	outputDir: string;
	mode: "batch" | "rescue";
	orcId?: number;
	oitId?: number;
	includeLayoutPng?: boolean;
	registerBatch?: boolean;
	layoutOutputDir?: string;
	onProgress?: (response: unknown) => void;
}

export interface ProductionBatchExportResult {
	success: boolean;
	outputDir: string;
	batchResult: unknown;
	registered: boolean;
	error?: string;
}

function isCorelOk(resp: unknown): boolean {
	if (!resp || typeof resp !== "object") return false;
	const status = String((resp as { status?: string }).status ?? "").toLowerCase();
	return status === "ok" || status === "success";
}

function getCorelMessage(resp: unknown): string {
	if (!resp || typeof resp !== "object") return "Erro desconhecido";
	const r = resp as { message?: string; error?: string };
	return r.message || r.error || "Erro na exportação";
}

/** Envia corel-export-batch e aguarda resultado final (extraído de useExport). */
export async function executeExportWithProgress(
	request: CorelExportRequest,
	onProgress?: (response: unknown) => void,
): Promise<unknown> {
	return new Promise((resolve, reject) => {
		let resolved = false;
		let maxTimeoutId: ReturnType<typeof setTimeout> | null = null;
		let silenceTimeoutId: ReturnType<typeof setTimeout> | null = null;

		const clearAllTimeouts = () => {
			if (maxTimeoutId) clearTimeout(maxTimeoutId);
			if (silenceTimeoutId) clearTimeout(silenceTimeoutId);
		};

		const resetSilenceTimeout = () => {
			if (silenceTimeoutId) clearTimeout(silenceTimeoutId);
			const silenceMinutes = Math.round(
				COREL_PROGRESS_SILENCE_TIMEOUT_MS / 60000,
			);
			silenceTimeoutId = setTimeout(() => {
				if (!resolved) {
					resolved = true;
					clearAllTimeouts();
					reject(
						new Error(
							`Timeout: sem resposta do C# por ${silenceMinutes} minutos`,
						),
					);
				}
			}, COREL_PROGRESS_SILENCE_TIMEOUT_MS);
		};

		const responseHandler = (response: unknown) => {
			if (resolved) return;

			const status = (response as { status?: string })?.status;

			if (status === "progress" || status === "progress_update") {
				onProgress?.(response);
				resetSilenceTimeout();
				return;
			}

			if (
				status === "ok" ||
				status === "error" ||
				status === "erro" ||
				status === "cancelled"
			) {
				resolved = true;
				clearAllTimeouts();

				if (status === "ok") {
					resolve(response);
				} else if (status === "cancelled") {
					reject(new Error("Exportação cancelada pelo usuário"));
				} else {
					const errorMsg =
						(response as { message?: string; error?: string })?.message ||
						(response as { error?: string })?.error ||
						"Erro na exportação";
					reject(new Error(errorMsg));
				}
			}
		};

		const exportTimeout = getActionTimeout(
			request.action || "corel-export-batch",
		);
		const maxTimeoutMinutes = Math.round(exportTimeout / 60000);

		const requestId = corelCommunication.sendActionGuarded(
			request.action,
			request.data,
			responseHandler,
			exportTimeout,
		);

		if (!requestId) {
			reject(new Error("Falha ao enviar comando de exportação"));
			return;
		}

		maxTimeoutId = setTimeout(() => {
			if (!resolved) {
				resolved = true;
				clearAllTimeouts();
				reject(
					new Error(
						`Timeout: operação excedeu limite máximo de ${maxTimeoutMinutes} minutos`,
					),
				);
			}
		}, exportTimeout);

		resetSilenceTimeout();
	});
}

async function sendLayoutPng(
	savedData: Record<string, unknown>,
	targets: Array<{
		staticId: string;
		guid: string;
		multiplicador?: number;
		contracao?: string;
	}>,
	requestId: string,
): Promise<unknown> {
	const payload = buildLayoutPngPayload(savedData, targets, requestId);
	return new Promise((resolve) => {
		try {
			corelCommunication.sendActionGuarded(
				payload.action,
				payload.data,
				(r: unknown) => resolve(r),
			);
		} catch (e) {
			resolve({ status: "error", error: String(e) });
		}
	});
}

/**
 * Motor compartilhado de exportação batch/rescue (produção).
 * Usado pelo fluxo offline e reutilizável pelo export normal.
 */
export async function runProductionBatchExport(
	params: ProductionBatchExportParams,
): Promise<ProductionBatchExportResult> {
	const {
		savedData,
		format,
		outputDir,
		mode,
		includeLayoutPng = false,
		registerBatch = true,
	} = params;

	const orcId =
		params.orcId ??
		Number((savedData?.pedido as { orc_id?: number } | undefined)?.orc_id ?? 0);
	const oitId =
		params.oitId ??
		Number((savedData?.pedido as { oit_id?: number } | undefined)?.oit_id ?? 0);

	const syncResult = await syncTargetsFromCorel({
		saved: savedData,
		targetsOverride: Array.isArray(savedData.objetos)
			? (savedData.objetos as unknown[])
			: null,
		monitoradorObjetos,
	});

	const targets = syncResult.targets;
	if (!targets.length) {
		throw new Error("Nenhum objeto disponível para exportação.");
	}

	const targetsWithNames = enrichTargetsWithNames(targets, savedData);
	const smb = extractSmbCredentials(savedData);
	const requestId = `${mode}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
	const idempotencyKey = buildIdempotencyKey(
		"batch",
		outputDir,
		orcId || "0",
		oitId || "0",
		targets,
	);

	const batchData: Record<string, unknown> = {
		mode,
		format,
		output_dir: outputDir,
		profile_id: 1,
		targets: targetsWithNames.map((t) => ({
			staticId: t.staticId,
			guid: t.guid,
			multiplicador: t.multiplicador,
			contracao: t.contracao,
			output_name: t.output_name,
		})),
		bbox: { mode: "tight", bleed_mm: 0 },
		overrides: {},
		idempotencyKey,
		...(smb ? { smb } : {}),
	};

	sendToTerminal(`📤 [PRODUCTION_BATCH] mode=${mode}`, {
		outputDir,
		format,
		targets: targetsWithNames.length,
	});

	const batchResp = await executeExportWithProgress(
		{
			action: "corel-export-batch",
			requestId,
			data: batchData,
		},
		params.onProgress,
	);

	if (!isCorelOk(batchResp)) {
		throw new Error(getCorelMessage(batchResp));
	}

	const batchResult =
		(batchResp as { result?: unknown }).result ?? batchResp;

	if (includeLayoutPng) {
		const pngRequestId = `${mode}_png_${Date.now()}`;
		const pngResp = await sendLayoutPng(savedData, targets, pngRequestId);
		if (!isCorelOk(pngResp)) {
			notify({
				type: "warning",
				title: "Layout PNG",
				message: `Produção OK, mas layout PNG falhou: ${getCorelMessage(pngResp)}`,
				duration: 8000,
			});
		}
	}

	let registered = false;
	if (registerBatch && orcId > 0 && oitId > 0) {
		const apiPayload = buildBatchPayload(
			savedData,
			batchResult,
			format,
			outputDir,
			targets,
		);
		if (apiPayload) {
			const postResult = await postBatchExport(apiPayload, "", idempotencyKey);
			registered = postResult.success;
			if (!registered) {
				notify({
					type: "warning",
					title: "Registro no sistema",
					message:
						postResult.message ||
						"Arquivos exportados localmente. Não foi possível registrar no sistema — registre manualmente se necessário.",
					duration: 8000,
				});
			}
		}
	}

	try {
		terminalLog.debug("[PRODUCTION_BATCH:COMPLETE]", {
			registered,
			orcId,
			oitId,
		});
	} catch {
		/* noop */
	}

	return {
		success: true,
		outputDir,
		batchResult,
		registered,
	};
}
